
import { Tractor, TestTube, Plane, Smartphone, Leaf, Droplets, Settings, ShoppingBag, GraduationCap, FileText, Phone, Mail, MapPin, User, Building } from "lucide-react";
import { Button } from "./ui/button";
import { motion } from "motion/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import React, { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";

export function ServicesGrid() {
  const { t } = useLanguage();
  const [openDialog, setOpenDialog] = useState(false);
  const [openSoilDialog, setOpenSoilDialog] = useState(false);
  const [openPrecisionDialog, setOpenPrecisionDialog] = useState(false);
  const [openDigitalAppDialog, setOpenDigitalAppDialog] = useState(false);
  const [openPlantationDialog, setOpenPlantationDialog] = useState(false);
  const [openIrrigationDialog, setOpenIrrigationDialog] = useState(false);
  const [openEquipmentDialog, setOpenEquipmentDialog] = useState(false);
  const [openMarketingDialog, setOpenMarketingDialog] = useState(false);
  const [openQuoteDialog, setOpenQuoteDialog] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  // listen for anchor/hash changes to reveal specific service card
  useEffect(() => {
    const closeAllDialogs = () => {
      setOpenDialog(false);
      setOpenSoilDialog(false);
      setOpenPrecisionDialog(false);
      setOpenDigitalAppDialog(false);
      setOpenPlantationDialog(false);
      setOpenIrrigationDialog(false);
      setOpenEquipmentDialog(false);
      setOpenMarketingDialog(false);
    };

    const openDialogById = (targetId: string) => {
      closeAllDialogs();
      switch (targetId) {
        case 'farm-development':
          setOpenDialog(true);
          break;
        case 'soil-testing':
          setOpenSoilDialog(true);
          break;
        case 'precision-agriculture':
          setOpenPrecisionDialog(true);
          break;
        case 'digital-farming':
          setOpenDigitalAppDialog(true);
          break;
        case 'plantation-nursery':
          setOpenPlantationDialog(true);
          break;
        case 'irrigation-solutions':
          setOpenIrrigationDialog(true);
          break;
        case 'equipment-supply':
          setOpenEquipmentDialog(true);
          break;
        case 'marketing-support':
          setOpenMarketingDialog(true);
          break;
        default:
          break;
      }
    };

    const reveal = (id?: string) => {
      const targetId = id ?? window.location.hash.replace('#', '');
      if (!targetId) return;

      const highlight = (element: HTMLElement) => {
        const offset = 120;
        const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
        try {
          window.scrollTo({ top, behavior: 'smooth' });
        } catch {}
        element.classList.add('ring-4');
        setTimeout(() => element.classList.remove('ring-4'), 3000);
      };

      const el = document.getElementById(targetId);
      if (el) {
        highlight(el);
        openDialogById(targetId);
        return;
      }

      // if the card isn't in DOM (collapsed), expand and retry
      setShowAllServices(true);
      setTimeout(() => {
        const elAfter = document.getElementById(targetId);
        if (elAfter) {
          highlight(elAfter);
          openDialogById(targetId);
        }
      }, 200);
    };

    const onHash = () => reveal();
    window.addEventListener('hashchange', onHash);

    // custom event from Footer
    const onOpenService = (e: Event) => {
      const detail = (e as CustomEvent)?.detail as { id?: string } | undefined;
      if (detail?.id) reveal(detail.id);
    };
    window.addEventListener('openService', onOpenService as EventListener);

    // allow other components to open the Quote dialog via CustomEvent
    const onOpenQuote = () => setOpenQuoteDialog(true);
    window.addEventListener('openQuote', onOpenQuote as EventListener);

    // reveal if page loaded with hash
    reveal();

    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('openService', onOpenService as EventListener);
      window.removeEventListener('openQuote', onOpenQuote as EventListener);
    };
  }, []);
  
  const handleQuoteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Quote request submitted successfully! Our team will contact you within 24 hours.", {
      duration: 5000,
    });
    setOpenQuoteDialog(false);
  };
  
  const services = [
    {
      id: 'farm-development',
      icon: Tractor,
      title: 'Complete Farm Development',
      description: 'Transform your land into a high-productivity agricultural asset',
      color: "from-[#235347] to-[#8EB69B]"
    },
    {
      id: 'soil-testing',
      icon: TestTube,
      title: 'Soil Testing & Crop Advisory',
      description: 'Scientific soil analysis and customized crop recommendations',
      color: "from-[#163832] to-[#235347]"
    },
    {
      id: 'precision-agriculture',
      icon: Plane,
      title: 'Precision Agriculture',
      description: 'Advanced technology for optimized farming operations',
      color: "from-[#8EB69B] to-[#C9A961]"
    },
    {
      id: 'digital-farming',
      icon: Smartphone,
      title: 'Digital Farming Solutions',
      description: 'Smart apps and IoT devices for modern farm management',
      color: "from-[#C9A961] to-[#8EB69B]"
    },
    {
      id: 'plantation-nursery',
      icon: Leaf,
      title: 'Nursery & Plantation',
      description: 'Premium quality saplings and plantation expertise',
      color: "from-[#235347] to-[#163832]"
    },
    {
      id: 'irrigation-solutions',
      icon: Droplets,
      title: 'Advanced Irrigation',
      description: 'Water-efficient irrigation systems and management',
      color: "from-[#0B2B26] to-[#235347]"
    },
    {
      id: 'equipment-supply',
      icon: Settings,
      title: 'Farm Machinery & Equipment',
      description: 'Modern agricultural equipment and tools supply',
      color: "from-[#163832] to-[#8EB69B]"
    },
    {
      id: 'marketing-support',
      icon: ShoppingBag,
      title: 'Marketing & Post-Harvest',
      description: 'Market linkage and value addition services',
      color: "from-[#8EB69B] to-[#235347]"
    },
    {
      id: 'training',
      icon: GraduationCap,
      title: 'Training',
      description: 'Farmer training programs, workshops, and ongoing technical support.',
      color: "from-[#235347] to-[#C9A961]"
    },
    {
      id: 'custom-projects',
      icon: FileText,
      title: 'Custom Projects & Subsidy',
      description: 'DPR reports, polyhouse setup, cold storage, and government subsidy guidance.',
      color: "from-[#C9A961] to-[#235347]"
    }
  ];

  const visibleServices = showAllServices ? services : services.slice(0, 6);

  return (
    <section id="services" className="py-20 px-6 lg:px-20 bg-gradient-to-b from-[#DAF1DE] via-white to-[#DAF1DE]/60 relative overflow-hidden">
      {/* Animated Background Elements */}
      <motion.div 
        className="absolute top-20 left-10 w-[500px] h-[500px] bg-[#8EB69B]/10 rounded-full blur-3xl"
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
          scale: [1, 1.2, 1]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div 
        className="absolute bottom-20 right-10 w-[600px] h-[600px] bg-[#C9A961]/10 rounded-full blur-3xl"
        animate={{
          x: [0, -100, 0],
          y: [0, -50, 0],
          scale: [1.2, 1, 1.2]
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-24">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block bg-gradient-to-r from-[#235347]/20 to-[#8EB69B]/20 px-8 py-4 rounded-full border-2 border-[#C9A961]/30 mb-8 backdrop-blur-sm shadow-xl"
          >
            <p className="text-[#051F20] tracking-[0.2em] uppercase text-sm">{t('services.title')}</p>
          </motion.div>
          
          <motion.h2 
            className="text-6xl lg:text-7xl text-[#051F20] mb-8 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {t('services.title')}
          </motion.h2>
          
          <motion.p 
            className="text-[#163832] text-xl max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            {t('services.subtitle')}
          </motion.p>
        </div>

        {/* Services Grid with Premium 3D */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {visibleServices.map((service, index) => (
            <motion.div
              id={service.id}
              key={service.id ?? index}
              initial={{ opacity: 0, y: 80, rotateX: -30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                delay: index * 0.08,
                duration: 0.7,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                y: -20,
                rotateX: 10,
                rotateY: 5,
                scale: 1.03,
                transition: { duration: 0.4 }
              }}
              style={{ 
                perspective: 1500,
                transformStyle: 'preserve-3d'
              }}
              className="group relative bg-white p-10 rounded-[32px] border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/60 shadow-xl hover:shadow-2xl transition-all duration-500"
            >
              {/* 3D Background Layer */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 rounded-[32px]`}
                whileHover={{ opacity: 0.05 }}
                style={{ transform: 'translateZ(-15px)' }}
              />
              
              {/* Premium Shine Effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-[#DAF1DE]/40 to-transparent rounded-[32px]"
                initial={{ x: '-100%', opacity: 0 }}
                whileHover={{ x: '100%', opacity: 1 }}
                transition={{ duration: 0.8 }}
              />
              
              {/* Glow Border */}
              <motion.div
                className={`absolute -inset-0.5 bg-gradient-to-r ${service.color} opacity-0 rounded-[32px] blur-xl`}
                whileHover={{ opacity: 0.3 }}
                transition={{ duration: 0.3 }}
              />
              
              <div className="relative z-10" style={{ transform: 'translateZ(20px)' }}>
                {/* Icon */}
                <motion.div 
                  className={`h-16 w-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 shadow-2xl relative overflow-hidden`}
                  whileHover={{ 
                    rotate: [0, -12, 12, 0],
                    scale: 1.1
                  }}
                  transition={{ duration: 0.6 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-tr from-white/30 via-white/10 to-transparent"
                    animate={{
                      opacity: [0.3, 0.6, 0.3],
                      rotate: 360
                    }}
                    transition={{
                      opacity: { duration: 2, repeat: Infinity },
                      rotate: { duration: 4, repeat: Infinity, ease: "linear" }
                    }}
                  />
                  <service.icon className="h-7 w-7 text-white relative z-10" />
                </motion.div>
                
                {/* Title */}
                <motion.h3 
                  className="text-[#051F20] text-2xl mb-4 group-hover:text-[#235347] transition-colors"
                  whileHover={{ x: 5 }}
                >
                  {service.title}
                </motion.h3>
                
                {/* Description */}
                <p className="text-[#163832]/80 leading-relaxed mb-6 text-lg">
                  {service.description}
                </p>
                
                {/* Learn More Link */}
                <motion.div
                  whileHover={{ x: 10 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Button 
                    variant="ghost" 
                    className="text-[#235347] hover:text-[#051F20] hover:bg-[#DAF1DE]/50 p-0 h-auto text-lg group/btn"
                    onClick={() => index === 0 && setOpenDialog(true) || index === 1 && setOpenSoilDialog(true) || index === 2 && setOpenPrecisionDialog(true) || index === 3 && setOpenDigitalAppDialog(true) || index === 4 && setOpenPlantationDialog(true) || index === 5 && setOpenIrrigationDialog(true) || index === 6 && setOpenEquipmentDialog(true) || index === 7 && setOpenMarketingDialog(true)}
                  >
                    {t('services.learnMore')}
                    <motion.span
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="inline-block ml-2"
                    >
                      →
                    </motion.span>
                  </Button>
                </motion.div>
              </div>
              
              {/* Corner Accent */}
              <div className="absolute bottom-5 right-5 w-16 h-16 border-b-2 border-r-2 border-[#C9A961]/30 group-hover:border-[#C9A961]/70 rounded-br-3xl transition-colors duration-300"></div>
            </motion.div>
          ))}
        </div>

        {/* Merged View Services Button */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <motion.div
            whileHover={{ scale: 1.05, y: -8 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              onClick={() => setShowAllServices(!showAllServices)}
              className="bg-gradient-to-r from-[#235347] via-[#163832] to-[#8EB69B] hover:from-[#051F20] hover:to-[#235347] text-white px-16 py-8 text-xl shadow-2xl hover:shadow-[#8EB69B]/40 transition-all duration-500 rounded-[24px] border-2 border-[#C9A961]/30 relative overflow-hidden group"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-[#C9A961]/30 to-transparent"
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 1 }}
              />
              <span className="relative z-10">{showAllServices ? t('common.readMore') : `${t('common.viewAll')} ${t('services.title')}`}</span>
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Premium Farm Development Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-[#DAF1DE]/80 to-white border-2 border-[#8EB69B]/30 rounded-3xl shadow-2xl [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <DialogHeader className="border-b border-[#8EB69B]/20 pb-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="h-16 w-16 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-2xl flex items-center justify-center shadow-lg">
                <Tractor className="h-8 w-8 text-white" />
              </div>
              <div>
                <DialogTitle className="text-4xl text-[#051F20] mb-2">
                  Farm Development & Planning
                </DialogTitle>
                <DialogDescription className="text-[#235347] text-lg">
                  End-to-End Smart Farm Setup for Maximum Productivity
                </DialogDescription>
              </div>
            </motion.div>
          </DialogHeader>

          <motion.div 
            className="space-y-8 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            {/* Introduction */}
            <div className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-8 rounded-2xl border border-[#8EB69B]/20">
              <p className="text-[#163832] text-lg leading-relaxed">
                Kalpakrushi provides complete farm development and planning solutions—from land assessment to full-scale infrastructure development. We design modern, efficient, and sustainable farms tailored to your land, crop, and regional conditions.
              </p>
            </div>

            {/* What We Deliver Section */}
            <div>
              <motion.h3 
                className="text-3xl text-[#051F20] mb-6 flex items-center gap-3"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                <div className="h-1.5 w-12 bg-gradient-to-r from-[#C9A961] to-[#8EB69B] rounded-full" />
                What We Deliver
              </motion.h3>

              <div className="grid grid-cols-1 gap-6">
                {/* Service 1 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Land Survey & Soil Profiling
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Scientific evaluation of soil, water sources, and land contouring.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 2 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Farm Layout Design
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Smart, scalable layouts for fruits, vegetables, integrated farming, polyhouse areas, irrigation systems, and pathways.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 3 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Land Preparation & Infrastructure
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Land leveling, fencing, borewell planning, drip irrigation setup, water storage solutions, and farm-road planning.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 4 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#163832] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Crop Planning & Plantation Models
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Data-driven crop selection, spacing models, multi-layer designs, and region-specific plantation strategies.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 5 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Complete Project Execution
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        On-ground project management until farm becomes fully operational.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div 
              className="bg-gradient-to-r from-[#235347] to-[#163832] p-8 rounded-2xl text-center"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-white text-2xl mb-4">
                Ready to Transform Your Farm?
              </h4>
              <p className="text-white/90 mb-6 text-lg">
                Contact us today for a free consultation and site assessment
              </p>
              <Button className="bg-gradient-to-r from-[#C9A961] to-[#8EB69B] hover:from-[#8EB69B] hover:to-[#C9A961] text-white px-10 py-6 text-lg shadow-xl rounded-xl">
                Get Started Now
              </Button>
            </motion.div>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Premium Soil Testing & Crop Advisory Dialog */}
      <Dialog open={openSoilDialog} onOpenChange={setOpenSoilDialog}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-[#DAF1DE]/80 to-white border-2 border-[#8EB69B]/30 rounded-3xl shadow-2xl [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <DialogHeader className="border-b border-[#8EB69B]/20 pb-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="h-16 w-16 bg-gradient-to-br from-[#163832] to-[#235347] rounded-2xl flex items-center justify-center shadow-lg">
                <TestTube className="h-8 w-8 text-white" />
              </div>
              <div>
                <DialogTitle className="text-4xl text-[#051F20] mb-2">
                  Soil Testing & Crop Advisory
                </DialogTitle>
                <DialogDescription className="text-[#235347] text-lg">
                  Scientific Insights for Higher Yields
                </DialogDescription>
              </div>
            </motion.div>
          </DialogHeader>

          <motion.div 
            className="space-y-8 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            {/* Introduction */}
            <div className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-8 rounded-2xl border border-[#8EB69B]/20">
              <p className="text-[#163832] text-lg leading-relaxed">
                Kalpakrushi provides accurate soil testing and expert agronomy guidance to help farmers make smarter, data-driven decisions. Our analysis reveals soil health, nutrient levels, pH balance, and micro-nutrient requirements to ensure the right crop and the right treatment.
              </p>
            </div>

            {/* What We Offer Section */}
            <div>
              <motion.h3 
                className="text-3xl text-[#051F20] mb-6 flex items-center gap-3"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                <div className="h-1.5 w-12 bg-gradient-to-r from-[#C9A961] to-[#8EB69B] rounded-full" />
                What We Offer
              </motion.h3>

              <div className="grid grid-cols-1 gap-6">
                {/* Service 1 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Comprehensive Soil Testing
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Detailed NPK, pH, micronutrient, organic carbon, and texture analysis.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 2 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Customized Crop Recommendations
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Region-specific and soil-specific crop planning for maximum profitability.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 3 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Fertilizer & Nutrient Plan
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Balanced nutrition schedule for healthier and faster plant growth.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 4 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#163832] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Soil Health Improvement Plan
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Organic amendments, bio-fertilizer suggestions, and soil-rejuvenation practices.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 5 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Expert Agronomist Support
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        On-call and on-field advisory for continuous monitoring.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div 
              className="bg-gradient-to-r from-[#235347] to-[#163832] p-8 rounded-2xl text-center"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-white text-2xl mb-4">
                Ready to Boost Your Yields?
              </h4>
              <p className="text-white/90 mb-6 text-lg">
                Contact us today for professional soil testing and expert crop advisory
              </p>
              <Button className="bg-gradient-to-r from-[#C9A961] to-[#8EB69B] hover:from-[#8EB69B] hover:to-[#C9A961] text-white px-10 py-6 text-lg shadow-xl rounded-xl">
                Get Started Now
              </Button>
            </motion.div>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Premium Precision Agriculture Dialog */}
      <Dialog open={openPrecisionDialog} onOpenChange={setOpenPrecisionDialog}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-[#DAF1DE]/80 to-white border-2 border-[#8EB69B]/30 rounded-3xl shadow-2xl [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <DialogHeader className="border-b border-[#8EB69B]/20 pb-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="h-16 w-16 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-2xl flex items-center justify-center shadow-lg">
                <Plane className="h-8 w-8 text-white" />
              </div>
              <div>
                <DialogTitle className="text-4xl text-[#051F20] mb-2">
                  Precision Agriculture
                </DialogTitle>
                <DialogDescription className="text-[#235347] text-lg">
                  Smart Technology for Smarter Farming
                </DialogDescription>
              </div>
            </motion.div>
          </DialogHeader>

          <motion.div 
            className="space-y-8 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            {/* Introduction */}
            <div className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-8 rounded-2xl border border-[#8EB69B]/20">
              <p className="text-[#163832] text-lg leading-relaxed">
                Kalpakrushi brings advanced precision agriculture solutions to enhance efficiency, reduce costs, and increase productivity. Using drones, sensors, and satellite-based analytics, we optimize every step of your farm operations.
              </p>
            </div>

            {/* What We Provide Section */}
            <div>
              <motion.h3 
                className="text-3xl text-[#051F20] mb-6 flex items-center gap-3"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                <div className="h-1.5 w-12 bg-gradient-to-r from-[#C9A961] to-[#8EB69B] rounded-full" />
                What We Provide
              </motion.h3>

              <div className="grid grid-cols-1 gap-6">
                {/* Service 1 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        Drone Spraying & Mapping
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        High-precision pesticide, fertilizer, and nano-urea spraying with uniform coverage.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 2 */}
                <motion.div
                  className="bg-white p-4 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        Satellite Monitoring & Crop Health Analysis
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        NDVI, moisture stress, pest detection, and growth tracking from space.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 3 */}
                <motion.div
                  className="bg-white p-4 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group h-full"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        IoT-Based Farm Monitoring
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        Real-time sensors for soil moisture, climate, and irrigation automation.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 4 */}
                <motion.div
                  className="bg-white p-4 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group h-full"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-[#163832] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        Data-Driven Advisory
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        Actionable insights for nutrient management, disease prediction, and yield enhancement.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 5 */}
                <motion.div
                  className="bg-white p-4 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group h-full"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-[#235347] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        Advanced Farm Reports
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        Visual maps, performance charts, and decision-making dashboards.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div 
              className="bg-gradient-to-r from-[#235347] to-[#163832] p-8 rounded-2xl text-center"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-white text-2xl mb-4">
                Ready to Transform Your Farm with Technology?
              </h4>
              <p className="text-white/90 mb-6 text-lg">
                Contact us today for advanced precision agriculture solutions
              </p>
              <Button className="bg-gradient-to-r from-[#C9A961] to-[#8EB69B] hover:from-[#8EB69B] hover:to-[#C9A961] text-white px-10 py-6 text-lg shadow-xl rounded-xl">
                Get Started Now
              </Button>
            </motion.div>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Premium Digital Farming App Dialog */}
      <Dialog open={openDigitalAppDialog} onOpenChange={setOpenDigitalAppDialog}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-[#DAF1DE]/80 to-white border-2 border-[#8EB69B]/30 rounded-3xl shadow-2xl [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <DialogHeader className="border-b border-[#8EB69B]/20 pb-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="h-16 w-16 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-2xl flex items-center justify-center shadow-lg">
                <Smartphone className="h-8 w-8 text-white" />
              </div>
              <div>
                <DialogTitle className="text-4xl text-[#051F20] mb-2">
                  Digital Farming App
                </DialogTitle>
                <DialogDescription className="text-[#235347] text-lg">
                  AI-Powered Farm Management in Your Pocket
                </DialogDescription>
              </div>
            </motion.div>
          </DialogHeader>

          <motion.div 
            className="space-y-8 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {/* Introduction */}
            <div className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-8 rounded-2xl border border-[#8EB69B]/20">
              <p className="text-[#163832] text-lg leading-relaxed">
                Kalpakrushi's Digital Farming App empowers farmers with smart tools, real-time updates, and complete farm management—anytime, anywhere. Designed for modern agriculture, the app simplifies decision-making and boosts productivity using data-driven insights.
              </p>
            </div>

            {/* Key Features Section */}
            <div>
              <motion.h3 
                className="text-3xl text-[#051F20] mb-6 flex items-center gap-3"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                <div className="h-1.5 w-12 bg-gradient-to-r from-[#C9A961] to-[#8EB69B] rounded-full" />
                Key Features
              </motion.h3>

              <div className="grid grid-cols-1 gap-4 auto-rows-fr">
                {/* Feature 1 */}
                <motion.div
                  className="bg-white p-4 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group h-full"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        AI-Based Crop Schedules
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Automated crop calendars, stage-wise tasks, and timely reminders.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Feature 2 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Advanced Weather Alerts
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Hyper-local forecasts, disease alerts, and rainfall updates.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Feature 3 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Smart Farm Management
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Track expenses, inputs, crop growth, irrigation, and field activities.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Feature 4 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#163832] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Daily Mandi Prices
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Accurate market rates from nearby APMCs and online trade options.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Feature 5 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Govt Schemes & Subsidies
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Updated agricultural schemes, eligibility info, and how-to guides.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Feature 6 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#8EB69B] to-[#163832] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Krishi Events & Training
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Notifications about agri exhibitions, workshops, and farmer meets.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div 
              className="bg-gradient-to-r from-[#235347] to-[#163832] p-8 rounded-2xl text-center"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-white text-2xl mb-4">
                Ready to Digitize Your Farm?
              </h4>
              <p className="text-white/90 mb-6 text-lg">
                Contact us today for the Kalpakrushi digital farming app
              </p>
              <Button className="bg-gradient-to-r from-[#C9A961] to-[#8EB69B] hover:from-[#8EB69B] hover:to-[#C9A961] text-white px-10 py-6 text-lg shadow-xl rounded-xl">
                Get Started Now
              </Button>
            </motion.div>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Premium Plantation & Nursery Services Dialog */}
      <Dialog open={openPlantationDialog} onOpenChange={setOpenPlantationDialog}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-[#DAF1DE]/80 to-white border-2 border-[#8EB69B]/30 rounded-3xl shadow-2xl [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <DialogHeader className="border-b border-[#8EB69B]/20 pb-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="h-16 w-16 bg-gradient-to-br from-[#235347] to-[#163832] rounded-2xl flex items-center justify-center shadow-lg">
                <Leaf className="h-8 w-8 text-white" />
              </div>
              <div>
                <DialogTitle className="text-4xl text-[#051F20] mb-2">
                  Plantation & Nursery Services
                </DialogTitle>
                <DialogDescription className="text-[#235347] text-lg">
                  Healthy Plants. Strong Farms. Sustainable Growth.
                </DialogDescription>
              </div>
            </motion.div>
          </DialogHeader>

          <motion.div 
            className="space-y-8 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {/* Introduction */}
            <div className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-8 rounded-2xl border border-[#8EB69B]/20">
              <p className="text-[#163832] text-lg leading-relaxed">
                Kalpakrushi delivers professional plantation and nursery solutions designed to improve survival rate, increase yield, and ensure long-term farm success. We provide high-quality saplings and scientific plantation models tailored to your region and farm goals.
              </p>
            </div>

            {/* What We Offer Section */}
            <div>
              <motion.h3 
                className="text-3xl text-[#051F20] mb-6 flex items-center gap-3"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                <div className="h-1.5 w-12 bg-gradient-to-r from-[#C9A961] to-[#8EB69B] rounded-full" />
                What We Offer
              </motion.h3>

              <div className="grid grid-cols-1 gap-6">
                {/* Service 1 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Premium Quality Saplings
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Certified, disease-free fruit, timber, medicinal, and horticulture plants.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 2 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Scientific Plantation Design
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Crop-wise spacing models, drip layout planning, and multi-layer plantation systems.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 3 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Customized Farm Plantation
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Region-specific crop selection and complete field preparation support.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 4 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#163832] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Nursery Setup & Management
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Polyhouse nurseries, shade-net structures, seedling production, and management guidance.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 5 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Growth Monitoring & Support
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Regular advisory, survival tracking, and aftercare solutions.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div 
              className="bg-gradient-to-r from-[#235347] to-[#163832] p-8 rounded-2xl text-center"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-white text-2xl mb-4">
                Ready to Build a Thriving Plantation?
              </h4>
              <p className="text-white/90 mb-6 text-lg">
                Contact us today for premium saplings and expert nursery solutions
              </p>
              <Button className="bg-gradient-to-r from-[#C9A961] to-[#8EB69B] hover:from-[#8EB69B] hover:to-[#C9A961] text-white px-10 py-6 text-lg shadow-xl rounded-xl">
                Get Started Now
              </Button>
            </motion.div>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Premium Water & Irrigation Solutions Dialog */}
      <Dialog open={openIrrigationDialog} onOpenChange={setOpenIrrigationDialog}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-[#DAF1DE]/80 to-white border-2 border-[#8EB69B]/30 rounded-3xl shadow-2xl [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <DialogHeader className="border-b border-[#8EB69B]/20 pb-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="h-16 w-16 bg-gradient-to-br from-[#0B2B26] to-[#235347] rounded-2xl flex items-center justify-center shadow-lg">
                <Droplets className="h-8 w-8 text-white" />
              </div>
              <div>
                <DialogTitle className="text-4xl text-[#051F20] mb-2">
                  Water & Irrigation Solutions
                </DialogTitle>
                <DialogDescription className="text-[#235347] text-lg">
                  Efficient Water Management for Sustainable Farming
                </DialogDescription>
              </div>
            </motion.div>
          </DialogHeader>

          <motion.div 
            className="space-y-8 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {/* Introduction */}
            <div className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-8 rounded-2xl border border-[#8EB69B]/20">
              <p className="text-[#163832] text-lg leading-relaxed">
                Kalpakrushi provides advanced irrigation and water conservation solutions that help farmers reduce costs, improve water use efficiency, and boost crop productivity. Our systems are designed for long-term durability, optimal performance, and minimal maintenance.
              </p>
            </div>

            {/* What We Offer Section */}
            <div>
              <motion.h3 
                className="text-3xl text-[#051F20] mb-6 flex items-center gap-3"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                <div className="h-1.5 w-12 bg-gradient-to-r from-[#C9A961] to-[#8EB69B] rounded-full" />
                What We Offer
              </motion.h3>

              <div className="grid grid-cols-1 gap-6">
                {/* Service 1 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Drip Irrigation Systems
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Precision watering for fruits, vegetables, and field crops to save water and increase yield.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 2 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Sprinkler & Rain-Gun Systems
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Uniform irrigation for large fields, nurseries, and fodder crops.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 3 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Rainwater Harvesting Solutions
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Farm ponds, storage tanks, bunds, and water recharge structures.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 4 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#163832] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Irrigation Layout & Automation
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Smart valve systems, filtration units, fertigation, and moisture-based automation.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 5 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Water Management Plans
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Scientific water budgeting, scheduling, and farm-level water efficiency strategies.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div 
              className="bg-gradient-to-r from-[#235347] to-[#163832] p-8 rounded-2xl text-center"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-white text-2xl mb-4">
                Ready to Save Water and Boost Productivity?
              </h4>
              <p className="text-white/90 mb-6 text-lg">
                Contact us today for advanced irrigation and water management solutions
              </p>
              <Button 
                className="bg-gradient-to-r from-[#C9A961] to-[#8EB69B] hover:from-[#8EB69B] hover:to-[#C9A961] text-white px-10 py-6 text-lg shadow-xl rounded-xl"
                onClick={() => setOpenQuoteDialog(true)}
              >
                Get a Quote
              </Button>
            </motion.div>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Premium Equipment & Machinery Supply Dialog */}
      <Dialog open={openEquipmentDialog} onOpenChange={setOpenEquipmentDialog}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-[#DAF1DE]/80 to-white border-2 border-[#8EB69B]/30 rounded-3xl shadow-2xl [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <DialogHeader className="border-b border-[#8EB69B]/20 pb-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="h-16 w-16 bg-gradient-to-br from-[#163832] to-[#8EB69B] rounded-2xl flex items-center justify-center shadow-lg">
                <Settings className="h-8 w-8 text-white" />
              </div>
              <div>
                <DialogTitle className="text-4xl text-[#051F20] leading-tight mb-1">
                  Equipment & Machinery Supply
                </DialogTitle>
                <DialogDescription className="text-[#235347] text-lg leading-snug mt-0.5">
                  Modern Tools for Modern Farming
                </DialogDescription>
              </div>
            </motion.div>
          </DialogHeader>

          <motion.div 
            className="space-y-8 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {/* Introduction */}
            <div className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-8 rounded-2xl border border-[#8EB69B]/20">
              <p className="text-[#163832] text-lg leading-relaxed">
                Kalpakrushi provides high-performance farming machinery and reliable equipment services to support every stage of agricultural operations. From advanced tools to rental services, we ensure farmers receive efficient, cost-effective, and hassle-free solutions.
              </p>
            </div>

            {/* What We Offer Section */}
            <div>
              <motion.h3 
                className="text-3xl text-[#051F20] mb-6 flex items-center gap-3"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                <div className="h-1.5 w-12 bg-gradient-to-r from-[#C9A961] to-[#8EB69B] rounded-full" />
                What We Offer
              </motion.h3>

              <div className="grid grid-cols-1 gap-6">
                {/* Service 1 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        Modern Farm Equipment
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        Power weeders, tillers, brush cutters, sprayers, harvesters, and more.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 2 */}
                <motion.div
                  className="bg-white p-4 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group h-full"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        Machinery Rental Services
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        Affordable rental options for tractors, rotavators, mini-harvesters, and land-prep equipment.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 3 */}
                <motion.div
                  className="bg-white p-4 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group h-full"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        Maintenance & Repair Support
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        Regular servicing, on-site repairs, and genuine spare parts.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 4 */}
                <motion.div
                  className="bg-white p-4 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group h-full"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-[#163832] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        Customized Machinery Recommendations
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        Based on land size, crop type, and farming goals.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 5 */}
                <motion.div
                  className="bg-white p-4 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group h-full"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-[#235347] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-lg text-[#051F20] mb-1">
                        Training & Demonstrations
                      </h4>
                      <p className="text-[#163832]/80 text-base leading-snug">
                        Hands-on training for safe and efficient equipment operation.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div 
              className="bg-gradient-to-r from-[#235347] to-[#163832] p-8 rounded-2xl text-center"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-white text-2xl mb-4">
                Ready to Modernize Your Farm Equipment?
              </h4>
              <p className="text-white/90 mb-6 text-lg">
                Contact us today for advanced machinery and equipment solutions
              </p>
              <Button className="bg-gradient-to-r from-[#C9A961] to-[#8EB69B] hover:from-[#8EB69B] hover:to-[#C9A961] text-white px-10 py-6 text-lg shadow-xl rounded-xl">
                Get Started Now
              </Button>
            </motion.div>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Premium Marketing & Post-Harvest Support Dialog */}
      <Dialog open={openMarketingDialog} onOpenChange={setOpenMarketingDialog}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-[#DAF1DE]/80 to-white border-2 border-[#8EB69B]/30 rounded-3xl shadow-2xl [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <DialogHeader className="border-b border-[#8EB69B]/20 pb-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="h-16 w-16 bg-gradient-to-br from-[#8EB69B] to-[#235347] rounded-2xl flex items-center justify-center shadow-lg">
                <ShoppingBag className="h-8 w-8 text-white" />
              </div>
              <div>
                <DialogTitle className="text-4xl text-[#051F20] mb-2">
                  Marketing & Post-Harvest Support
                </DialogTitle>
                <DialogDescription className="text-[#235347] text-lg">
                  Helping Farmers Earn More from Every Harvest
                </DialogDescription>
              </div>
            </motion.div>
          </DialogHeader>

          <motion.div 
            className="space-y-8 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {/* Introduction */}
            <div className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-8 rounded-2xl border border-[#8EB69B]/20">
              <p className="text-[#163832] text-lg leading-relaxed">
                Kalpakrushi provides end-to-end marketing and post-harvest services to help farmers increase profits, reduce wastage, and find the right buyers. Our solutions focus on branding, value addition, and strong market linkages.
              </p>
            </div>

            {/* What We Offer Section */}
            <div>
              <motion.h3 
                className="text-3xl text-[#051F20] mb-6 flex items-center gap-3"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                <div className="h-1.5 w-12 bg-gradient-to-r from-[#C9A961] to-[#8EB69B] rounded-full" />
                What We Offer
              </motion.h3>

              <div className="grid grid-cols-1 gap-6">
                {/* Service 1 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Product Branding & Packaging
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Premium brand creation, label design, and value-added packaging for farm products.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 2 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Market Linkage Support
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Direct buyer connections, wholesale partnerships, and retail distribution channels.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 3 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Value Addition Services
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Processing, grading, sorting, dehydration, and product standardization.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 4 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#163832] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Post-Harvest Management
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Scientific storage, cold chain support, and shelf-life extension practices.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Service 5 */}
                <motion.div
                  className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 shadow-lg hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <div className="h-2 w-2 bg-white rounded-full" />
                    </div>
                    <div>
                      <h4 className="text-xl text-[#051F20] mb-2">
                        Business Growth Advisory
                      </h4>
                      <p className="text-[#163832]/80 leading-relaxed">
                        Pricing strategy, sales planning, and market intelligence.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div 
              className="bg-gradient-to-r from-[#235347] to-[#163832] p-8 rounded-2xl text-center"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-white text-2xl mb-4">
                Ready to Maximize Your Farm Revenue?
              </h4>
              <p className="text-white/90 mb-6 text-lg">
                Contact us today for comprehensive marketing and post-harvest solutions
              </p>
              <Button className="bg-gradient-to-r from-[#C9A961] to-[#8EB69B] hover:from-[#8EB69B] hover:to-[#C9A961] text-white px-10 py-6 text-lg shadow-xl rounded-xl">
                Get Started Now
              </Button>
            </motion.div>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Premium Quote Request Dialog */}
      <Dialog open={openQuoteDialog} onOpenChange={setOpenQuoteDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-[#DAF1DE]/80 to-white border-2 border-[#8EB69B]/30 rounded-3xl shadow-2xl [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <DialogHeader className="border-b border-[#8EB69B]/20 pb-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="h-16 w-16 bg-gradient-to-br from-[#C9A961] to-[#8EB69B] rounded-2xl flex items-center justify-center shadow-lg">
                <Droplets className="h-8 w-8 text-white" />
              </div>
              <div>
                <DialogTitle className="text-4xl text-[#051F20] mb-2">
                  Request a Quote
                </DialogTitle>
                <DialogDescription className="text-[#235347] text-lg">
                  Get a Customized Irrigation Solution for Your Farm
                </DialogDescription>
              </div>
            </motion.div>
          </DialogHeader>

          <motion.div 
            className="space-y-6 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {/* Introduction */}
            <div className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-6 rounded-2xl border border-[#8EB69B]/20">
              <p className="text-[#163832] leading-relaxed">
                Fill out the form below and our irrigation experts will get back to you within 24 hours with a detailed quote tailored to your farm's needs.
              </p>
            </div>

            {/* Form */}
            <form className="space-y-6" onSubmit={handleQuoteSubmit}>
              {/* Personal Information Section */}
              <motion.div 
                className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 shadow-lg"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className="text-2xl text-[#051F20] mb-6 flex items-center gap-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center shadow-md">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[#051F20]">Full Name *</label>
                    <Input 
                      placeholder="Enter your full name" 
                      className="border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 px-4 text-[#163832]"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-[#051F20]">Phone Number *</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8EB69B]" />
                      <Input 
                        type="tel"
                        placeholder="+91 98765 43210" 
                        className="border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 pl-12 pr-4 text-[#163832]"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[#051F20]">Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8EB69B]" />
                      <Input 
                        type="email"
                        placeholder="your.email@example.com" 
                        className="border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 pl-12 pr-4 text-[#163832]"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[#051F20]">Farm Location *</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8EB69B]" />
                      <Input 
                        placeholder="City, District, State" 
                        className="border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 pl-12 pr-4 text-[#163832]"
                        required
                      />
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Farm Details Section */}
              <motion.div 
                className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 shadow-lg"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <h3 className="text-2xl text-[#051F20] mb-6 flex items-center gap-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center shadow-md">
                    <Leaf className="h-5 w-5 text-white" />
                  </div>
                  Farm Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[#051F20]">Farm Size (in Acres) *</label>
                    <Input 
                      type="number"
                      placeholder="e.g., 5" 
                      className="border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 px-4 text-[#163832]"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[#051F20]">Crop Type *</label>
                    <Input 
                      placeholder="e.g., Vegetables, Fruits, Grains" 
                      className="border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 px-4 text-[#163832]"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[#051F20]">Water Source *</label>
                    <select 
                      className="w-full border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 px-4 text-[#163832] bg-white"
                      required
                    >
                      <option value="">Select water source</option>
                      <option value="borewell">Borewell</option>
                      <option value="canal">Canal</option>
                      <option value="river">River/Stream</option>
                      <option value="pond">Farm Pond</option>
                      <option value="rainwater">Rainwater Harvesting</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[#051F20]">Irrigation Type Needed *</label>
                    <select 
                      className="w-full border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 px-4 text-[#163832] bg-white"
                      required
                    >
                      <option value="">Select irrigation type</option>
                      <option value="drip">Drip Irrigation</option>
                      <option value="sprinkler">Sprinkler System</option>
                      <option value="raingun">Rain Gun</option>
                      <option value="micro">Micro Sprinkler</option>
                      <option value="combination">Combination System</option>
                      <option value="consulting">Need Consultation</option>
                    </select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-[#051F20]">Additional Requirements</label>
                    <Textarea 
                      placeholder="Tell us more about your specific needs, budget constraints, timeline, or any questions you have..."
                      className="border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl px-4 py-3 text-[#163832] min-h-[120px]"
                      rows={5}
                    />
                  </div>
                </div>
              </motion.div>

              {/* Budget & Timeline Section */}
              <motion.div 
                className="bg-white p-6 rounded-2xl border-2 border-[#8EB69B]/20 shadow-lg"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <h3 className="text-2xl text-[#051F20] mb-6 flex items-center gap-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center shadow-md">
                    <Building className="h-5 w-5 text-white" />
                  </div>
                  Budget & Timeline
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[#051F20]">Estimated Budget Range</label>
                    <select 
                      className="w-full border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 px-4 text-[#163832] bg-white"
                    >
                      <option value="">Select budget range</option>
                      <option value="50k-1l">₹50,000 - ₹1,00,000</option>
                      <option value="1l-2l">₹1,00,000 - ₹2,00,000</option>
                      <option value="2l-5l">₹2,00,000 - ₹5,00,000</option>
                      <option value="5l-10l">₹5,00,000 - ₹10,00,000</option>
                      <option value="10l+">Above ₹10,00,000</option>
                      <option value="flexible">Flexible / Need Recommendation</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[#051F20]">Expected Installation Timeline</label>
                    <select 
                      className="w-full border-2 border-[#8EB69B]/30 focus:border-[#C9A961] rounded-xl h-12 px-4 text-[#163832] bg-white"
                    >
                      <option value="">Select timeline</option>
                      <option value="urgent">Urgent (Within 1 Month)</option>
                      <option value="1-3months">1-3 Months</option>
                      <option value="3-6months">3-6 Months</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </div>
                </div>
              </motion.div>

              {/* Submit Button */}
              <motion.div 
                className="flex justify-center pt-4"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    type="submit"
                    className="bg-gradient-to-r from-[#235347] via-[#163832] to-[#8EB69B] hover:from-[#051F20] hover:to-[#235347] text-white px-16 py-7 text-xl shadow-2xl hover:shadow-[#8EB69B]/40 transition-all duration-500 rounded-2xl border-2 border-[#C9A961]/30 relative overflow-hidden group"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-[#C9A961]/30 to-transparent"
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 1 }}
                    />
                    <span className="relative z-10 flex items-center gap-3">
                      <Droplets className="h-6 w-6" />
                      Submit Quote Request
                    </span>
                  </Button>
                </motion.div>
              </motion.div>

              {/* Contact Info */}
              <motion.div 
                className="bg-gradient-to-r from-[#DAF1DE]/40 to-[#8EB69B]/10 p-6 rounded-2xl border border-[#8EB69B]/20 text-center"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <p className="text-[#163832] mb-3">
                  <strong className="text-[#051F20]">Need immediate assistance?</strong>
                </p>
                <div className="flex flex-wrap justify-center gap-6 text-[#235347]">
                  <a href="tel:+918147851696" className="flex items-center gap-2 hover:text-[#C9A961] transition-colors">
                    <Phone className="h-5 w-5" />
                    <span>+91 81478 51696</span>
                  </a>
                  <a href="tel:+918088841428" className="flex items-center gap-2 hover:text-[#C9A961] transition-colors">
                    <Phone className="h-5 w-5" />
                    <span>+91 80888 41428</span>
                  </a>
                  <a href="mailto:info@kalpakrushi.com" className="flex items-center gap-2 hover:text-[#C9A961] transition-colors">
                    <Mail className="h-5 w-5" />
                    <span>info@kalpakrushi.com</span>
                  </a>
                </div>
              </motion.div>
            </form>
          </motion.div>
        </DialogContent>
      </Dialog>
    </section>
  );
}